import { useQuery, useQueryClient } from '@tanstack/react-query';
import { agentClient } from '../lib/connect/clients';
import { convertProtoConversation } from '../lib/connect/protoToChatHistory';
/**
 * Strip the legacy `shanks-` chatId prefix before any AgentService RPC.
 * Mirrors the same helper in ChatHistory — the pangaea collab.conversations
 * row and the shanks AgentService both key off the bare uuid; the prefix is
 * a wire-level affordance for routing in the frontend (so /assistant/<id>
 * still works after the cut from express).
 */ export function bareChatId(chatId) {
    return chatId.startsWith('shanks-') ? chatId.slice(7) : chatId;
}
/**
 * Build the React Query cache key used everywhere we fetch a conversation
 * by id. Centralised so the modal's optimistic write reaches every reader
 * that uses this hook — if you fetch a conversation outside this hook,
 * use this constant for your queryKey or your view won't update on Save.
 */ export function conversationQueryKey(chatId) {
    return [
        'conversation',
        bareChatId(chatId)
    ];
}
/**
 * Seed the cache key useConversation reads from. Call this from upstream
 * thread-fetcher queryFns (Chat.tsx, ProjectChatView, ProjectChatPage,
 * AISidebarShell) — they already have the full GetConversationResponse in
 * hand; stashing it under our key means useConversation's own fetch
 * deduplicates against the cached value rather than firing a second RPC
 * for the same payload.
 *
 * No-ops on falsy chatId or a response missing the conversation field, so
 * callers don't need to guard.
 */ export function primeConversationCache(queryClient, chatId, resp) {
    if (!chatId || !resp?.conversation) return;
    queryClient.setQueryData(conversationQueryKey(chatId), resp);
}
/**
 * Fetch a conversation from AgentService and surface its title +
 * description for header / settings rendering. Shared between the chat
 * header (live title display) and the ChatSettings modal so a Save in
 * the modal lights up the header instantly via `queryClient.setQueryData`.
 *
 * Pass an empty chatId to no-op (returns isLoading=false, no fetch fires).
 * That matches the brief window where ChatView mounts before a new chat
 * has minted an id.
 *
 * staleTime is 60s rather than 0: upstream thread fetchers run on every
 * mount with refetchOnMount: 'always' (they need the latest messages to
 * pick up artifact content), but the title + description don't change
 * that frequently and the ChatSettings save path invalidates this key
 * directly when they do. A 60s window lets the cache served by
 * primeConversationCache survive ChatView remounts within a single
 * navigation, so opening Chat Settings right after landing doesn't
 * fire a duplicate fetch.
 */ export function useConversation(chatId) {
    const bareId = bareChatId(chatId);
    return useQuery({
        queryKey: conversationQueryKey(chatId),
        queryFn: ()=>agentClient.getConversation({
                id: bareId
            }),
        enabled: bareId !== '',
        staleTime: 60000,
        refetchInterval: (query)=>query.state.data?.submissions?.some((submission)=>submission.state === 'running') ? 2000 : false
    });
}
/** Load saved transcripts; a known new draft has no server history to read yet. */ export function useChatHistory(chatId, allowMissing = false) {
    const queryClient = useQueryClient();
    const id = bareChatId(chatId ?? '');
    return useQuery({
        queryKey: [
            'chat-transcript',
            id
        ],
        queryFn: async ()=>{
            const response = await agentClient.getConversation({
                id
            });
            primeConversationCache(queryClient, id, response);
            return {
                ...convertProtoConversation(response.conversation, response.messages),
                readOnly: response.readOnly,
                submissions: response.submissions
            };
        },
        enabled: !!id && !allowMissing,
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
        refetchInterval: (query)=>query.state.data?.submissions?.some((submission)=>submission.state === 'running') ? 1500 : false,
        retry: false
    });
}
